import type { ComponentProps } from 'react';
import React, { useCallback, useRef } from 'react';
import type { GestureResponderEvent, TextStyle } from 'react-native';
import * as WebBrowser from 'expo-web-browser';

import { usePressableState } from '../../hooks';
import { BodyText } from '../Text/BodyText';
import type { TextLinkPalette } from './TextLink.styles';
import { getConditionalStyles } from './TextLink.styles';

// ────────────────────────────────────────────────────────────────────────────────

export const TextLink = (props: TextLinkProps) => {
  const {
    children,
    href,
    newTab,
    disabled,
    active,
    onPress,
    palette = 'primary',
    style: externalStyle,
    underline = true,
    ...restProps
  } = props;

  const textLinkRef = useRef(null);
  const pressableState = usePressableState(textLinkRef);
  const hasOnPress = href !== undefined || onPress !== undefined;

  // ─── STYLES ──────────────────────────────────────────────────────

  const conditionalStyles = getConditionalStyles({
    pressableState,
    palette,
    disabled,
    active,
    hasOnPress,
  });
  const style = [conditionalStyles, externalStyle];

  // ─── HELPERS ─────────────────────────────────────────────────────

  const handleOnPress = useCallback(
    async (event: GestureResponderEvent) => {
      onPress?.(event);

      if (href) {
        await WebBrowser.openBrowserAsync(href, {
          windowFeatures: newTab ? { target: '_blank' } : undefined,
        });
      }
    },
    [newTab, href, onPress],
  );

  // ─────────────────────────────────────────────────────────────────

  return (
    <BodyText
      accessibilityRole="button"
      ref={textLinkRef}
      style={style}
      onPress={hasOnPress ? handleOnPress : undefined}
      underline={underline}
      accessibilityState={{ disabled }}
      {...restProps}
    >
      {children}
    </BodyText>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type TextLinkProps = Readonly<{
  href?: string;
  newTab?: boolean;
  palette?: TextLinkPalette;
  style?: TextStyle;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
}> &
  ComponentProps<typeof BodyText>;
